import {
  GuestWaitingList,
  guestKeys,
  LookEntity,
  SurveyQuery,
  ListEntityV2,
  SurveyInstanceEntity,
  AttendeesInfoValidator,
  SurveyAnswersValidator,
  SurveyAnswersResponse,
  AxiosAPIError,
  usersKeys,
  AcceptWaitingListValidator,
} from '@seaters/constants';
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  acceptGuestWaitingList,
  exportGuestWaitingListParkingSeats,
  exportGuestWaitingListSeats,
  fetchGuestFanGroup,
  fetchGuestSurvey,
  fetchGuestWaitingList,
  guestCancelWaitingList,
  reExportGuestVoucher,
  rejectGuestWaitingList,
  sendGuestIxpoleTicketLink,
  sendGuestAnswers,
  updateGuestAttendeeInfo,
  updateGuestWaitingListSeats,
} from '../api';
import { traceKeyFlow } from '../utils/traceKeyFlow';

// `enabled` is required, not optional with a default: whether there is an invitation
// to load is the caller's question, and both of these fire only as a pair. A default
// here would be a second definition of that predicate, free to drift from the one the
// caller actually renders against.
export const useFetchGuestFanGroup = (
  slug: string,
  params: { code: string },
  { enabled }: { enabled: boolean }
) =>
  useQuery<LookEntity, Error>({
    queryKey: usersKeys.look(slug),
    queryFn: () => fetchGuestFanGroup(slug, params),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useFetchGuestWaitingList = (
  waitingListId: string,
  params: { code: string },
  { enabled }: { enabled: boolean }
) =>
  useQuery<GuestWaitingList, Error>({
    queryKey: guestKeys.waitingList(waitingListId),
    queryFn: () => fetchGuestWaitingList(waitingListId, params),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useRejectGuestWaitingList = (
  waitingListId: string,
  params: { code: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      traceKeyFlow('guest_invitation', () =>
        rejectGuestWaitingList(waitingListId, params)
      ),
    mutationKey: guestKeys.rejectMutation(),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

export const useAcceptGuestWaitingList = (
  waitingListId: string,
  params: { code: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      traceKeyFlow('guest_invitation', () =>
        acceptGuestWaitingList(waitingListId, params)
      ),
    mutationKey: guestKeys.acceptMutation(),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

export const useUpdateGuestWaitingListSeats = (
  waitingListId: string,
  params: { code: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nbrOfSeats }: { nbrOfSeats: number }) =>
      updateGuestWaitingListSeats(waitingListId, nbrOfSeats, params),
    mutationKey: guestKeys.nbrOfSeatsMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

export const useExportGuestWaitingListSeats = (
  waitingListId: string,
  params: { code: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => exportGuestWaitingListSeats(waitingListId, params),
    mutationKey: guestKeys.seatsExportMutation(),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

export const useExportGuestWaitingListParkingSeats = (
  waitingListId: string,
  params: { code: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => exportGuestWaitingListParkingSeats(waitingListId, params),
    mutationKey: guestKeys.parkingSeatsExportMutation(),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

// fetchGuestSurvey;
export const useFetchGuestSurvey = (params: SurveyQuery & { code: string }) =>
  useQuery<ListEntityV2<SurveyInstanceEntity>, Error>({
    queryKey: guestKeys.surveysList(params.waitinglist_id),
    queryFn: () => fetchGuestSurvey(params),
    placeholderData: keepPreviousData,
  });

// sendGuestAnswers;
export const useSendGuestAnswers = (
  instanceId: string,
  params: { code: string }
): UseMutationResult<
  SurveyAnswersResponse,
  AxiosAPIError<SurveyAnswersValidator>,
  SurveyAnswersValidator,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SurveyAnswersValidator) =>
      sendGuestAnswers(instanceId, body, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(instanceId),
      });
    },
  });
};

// useUpdateGuestAttendeeInfo;
export const useUpdateGuestAttendeeInfo = (
  waitingListId: string,
  params: { code: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ attendeeInfo }: { attendeeInfo: AttendeesInfoValidator }) =>
      updateGuestAttendeeInfo(waitingListId, attendeeInfo, params),
    mutationKey: guestKeys.attendeeInfoMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

export const useSendGuestIxpoleTicketLink = (
  waitingListId: string,
  params: { code: string }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ attendeeSlot }: { attendeeSlot: number }) =>
      sendGuestIxpoleTicketLink(waitingListId, attendeeSlot, params),
    mutationKey: guestKeys.sendTicketLinkMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

export const useReexportGuestVoucher = (
  waitingListId: string,
  params: { code: string }
): UseMutationResult<
  unknown,
  AxiosAPIError<AcceptWaitingListValidator>,
  AcceptWaitingListValidator,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AcceptWaitingListValidator) =>
      reExportGuestVoucher(waitingListId, body, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};

export const useGuestCancelWaitingList = (
  waitingListId: string,
  params: { code: string }
): UseMutationResult<
  null,
  AxiosAPIError<GuestWaitingList>,
  { waitingListId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => guestCancelWaitingList(waitingListId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: guestKeys.waitingList(waitingListId),
      });
    },
  });
};
